import { useEffect, useRef, useState } from "react";
import { appConfig } from "../apiConfig";
import { useLanguage } from "../context/LanguageProvider";

const CITIES = {
  Casablanca: { lat: 33.5731, lng: -7.5898 },
};

const ORIGIN = CITIES.Casablanca;

function decodePolyline(encoded) {
  let index = 0;
  let lat = 0;
  let lng = 0;
  const coords = [];

  while (index < encoded.length) {
    let b;
    let shift = 0;
    let result = 0;

    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);

    lat += result & 1 ? ~(result >> 1) : result >> 1;

    shift = 0;
    result = 0;

    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);

    lng += result & 1 ? ~(result >> 1) : result >> 1;

    coords.push([lat / 1e5, lng / 1e5]);
  }

  return coords;
}

async function fetchRoute(origin, coordinates, signal) {
  const url = `https://router.project-osrm.org/route/v1/driving/${origin.lng},${origin.lat};${coordinates[0]},${coordinates[1]}?overview=full&geometries=polyline`;

  const res = await fetch(url, { signal });

  if (!res.ok) {
    throw new Error("Failed to fetch route");
  }

  const data = await res.json();

  if (data.code !== "Ok" || !data.routes?.length) {
    return null;
  }

  const route = data.routes[0];

  return {
    coords: decodePolyline(route.geometry),
    distance: (route.distance / 1000).toFixed(0) + " km",
  };
}

function escapeHtml(value) {
  return String(value ?? "").replace(/[&<>"']/g, (char) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  })[char]);
}

export default function MapDirection({ city, coordinates }) {
  const { t } = useLanguage();

  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const routeLayerRef = useRef(null);
  const destMarkerRef = useRef(null);
  const infoRef = useRef(null);

  const [mapReady, setMapReady] = useState(false);

  // Load Leaflet CSS and JS, then initialize the map
  useEffect(() => {
    let cancelled = false;

    const initializeMap = () => {
      if (cancelled || !mapRef.current || !window.L) return;

      if (mapInstance.current) return;

      const L = window.L;

      const map = L.map(mapRef.current, {
        center: [31.7917, -7.0926],
        zoom: 6,
        zoomControl: true,
      });

      // OpenStreetMap tiles - No API key required
      L.tileLayer(
        "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
        {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          maxZoom: 19,
        }
      ).addTo(map);

      // Casablanca origin marker
      const originIcon = L.divIcon({
        className: "",
        html: `
          <div style="
            width: 32px;
            height: 32px;
            background: white;
            border-radius: 50%;
            border: 3px solid white;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 12px rgba(29,78,216,0.35);
          ">
            <img
              src="/images/${appConfig.slug}-fav.png"
              style="width:20px;height:20px;object-fit:contain;"
            />
          </div>
        `,
        iconSize: [38, 38],
        iconAnchor: [19, 19],
        popupAnchor: [0, -18],
      });

      L.marker([ORIGIN.lat, ORIGIN.lng], {
        icon: originIcon,
      })
        .addTo(map)
        .bindPopup(`
          <b>${escapeHtml(t("cors_map.casablanca"))}</b>
          <br />
          ${escapeHtml(appConfig.name)}
        `);

      mapInstance.current = map;

      setMapReady(true);

      setTimeout(() => {
        if (!cancelled) map.invalidateSize();
      }, 100);
    };

    // Load CSS
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");

      link.id = "leaflet-css";
      link.rel = "stylesheet";
      link.href =
        "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";

      document.head.appendChild(link);
    }

    if (!document.getElementById("map-direction-styles")) {
      const style = document.createElement("style");

      style.id = "map-direction-styles";
      style.textContent = `
        .destination-tooltip {
          padding: 4px 8px !important;
          border-radius: 8px !important;
          border: 1px solid #e5e7eb !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15) !important;
          background-color: white !important;
          color: #111827 !important;
          font-size: 11px !important;
          font-weight: 600 !important;
          line-height: 1 !important;
        }
        .destination-tooltip::before {
          display: none !important;
        }
      `;

      document.head.appendChild(style);
    }

    // Load JS
    if (window.L) {
      initializeMap();
    } else {
      let script = document.getElementById("leaflet-js");

      if (!script) {
        script = document.createElement("script");
        script.id = "leaflet-js";
        script.src =
          "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";

        document.head.appendChild(script);
      }

      script.addEventListener("load", initializeMap);

      // Handle an already-loaded script
      if (window.L) initializeMap();
    }

    return () => {
      cancelled = true;

      const script = document.getElementById("leaflet-js");
      script?.removeEventListener("load", initializeMap);

      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }

      setMapReady(false);
    };
  }, [t]);

  // Draw route whenever city or coordinates change
  useEffect(() => {
    const map = mapInstance.current;
    const L = window.L;

    if (!mapReady || !map || !L || !city || !coordinates) {
      return;
    }

    if (
      !Array.isArray(coordinates) ||
      coordinates.length < 2 ||
      !Number.isFinite(Number(coordinates[0])) ||
      !Number.isFinite(Number(coordinates[1]))
    ) {
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    const drawRoute = async () => {
      // Remove previous route
      if (routeLayerRef.current) {
        map.removeLayer(routeLayerRef.current);
        routeLayerRef.current = null;
      }

      // Remove previous destination marker
      if (destMarkerRef.current) {
        map.removeLayer(destMarkerRef.current);
        destMarkerRef.current = null;
      }

      if (infoRef.current) {
        infoRef.current.innerHTML = `
          <div style="padding:14px;">
            ${escapeHtml(t("cors_map.loading_route"))}
          </div>
        `;

        infoRef.current.style.display = "block";
      }

      try {
        const route = await fetchRoute(
          ORIGIN,
          coordinates,
          controller.signal
        );

        if (cancelled || !route) return;

        // Route outline
        const outline = L.polyline(route.coords, {
          color: "#93c5fd",
          weight: 9,
          opacity: 0.3,
          lineJoin: "round",
          lineCap: "round",
        });

        // Main route
        const polyline = L.polyline(route.coords, {
          color: "#2563eb",
          weight: 5,
          opacity: 0.9,
          lineJoin: "round",
          lineCap: "round",
        });

        // Add both lines as one layer group
        routeLayerRef.current = L.layerGroup([
          outline,
          polyline,
        ]).addTo(map);

        // Destination marker
        const destIcon = L.divIcon({
          className: "",
          html: `
            <div style="
              width: 20px;
              height: 20px;
              background: #dc2626;
              border-radius: 50% 50% 50% 0;
              transform: rotate(-45deg);
              border: 3px solid white;
              box-shadow: 0 3px 12px rgba(220,38,38,0.5);
            "></div>
          `,
          iconSize: [36, 36],
          iconAnchor: [12, 12],
          popupAnchor: [6, -12],
        });

        const marker = L.marker(
          [coordinates[1], coordinates[0]],
          { icon: destIcon }
        )
          .addTo(map)
          .bindTooltip(
            `
              <span style="
                display: inline-flex;
                padding: 2px 4px;
                align-items: center;
                gap: 6px;
                white-space: nowrap;
              ">
                ${escapeHtml(city)}
                <span style="
                  background: #eff6ff;
                  color: #1d4ed8;
                  padding: 2px 6px;
                  border-radius: 10px;
                  font-size: 10px;
                  font-weight: 700;
                ">${escapeHtml(route.distance)}</span>
              </span>
            `,
            {
              permanent: true,
              direction: "right",
              offset: [14, 0],
              className: "destination-tooltip",
              opacity: 1,
            }
          );

        destMarkerRef.current = marker;

        // Fit map to route
        map.fitBounds(polyline.getBounds(), {
          padding: [50, 50],
          maxZoom: 12,
        });

        // Route information
        if (infoRef.current) {
          infoRef.current.innerHTML = `
            <div style="
              padding: 14px;
              background: white;
              width: 100%;
              border-top-left-radius: 16px;
              border-top-right-radius: 16px;
            ">
              <h2 style="
                font-size: 1.2rem;
                font-weight: 700;
                color: #111827;
                margin-bottom: 8px;
              ">
                ${escapeHtml(t("cors_map.route_title"))}
              </h2>

              <div style="
                display: flex;
                align-items: center;
                flex-wrap: wrap;
                gap: 6px;
                font-size: 13px;
              ">
                <span style="
                  display: flex;
                  align-items: center;
                  gap: 4px;
                ">
                  <img
                    src="/images/${appConfig.slug}-fav.png"
                    style="width:18px;height:18px;object-fit:contain;"
                  />
                  ${escapeHtml(t("cors_map.casablanca"))}
                </span>

                <span style="color:#6b7280;margin:0 4px;">
                  →
                </span>

                <span>
                  📍 ${escapeHtml(city)}
                </span>

                <span style="
                  margin-left: 8px;
                  background: #eff6ff;
                  color: #1d4ed8;
                  padding: 3px 8px;
                  border-radius: 20px;
                  font-size: 12px;
                  font-weight: 600;
                ">
                  ${route.distance}
                </span>
              </div>
            </div>
          `;
        }
      } catch (error) {
        if (error.name === "AbortError" || cancelled) return;

        console.error("Route error:", error);

        if (infoRef.current) {
          infoRef.current.innerHTML = `
            <div style="padding:14px;color:#dc2626;">
              ${escapeHtml(t("cors_map.loading_route"))}
            </div>
          `;
        }
      }
    };

    drawRoute();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [mapReady, city, coordinates, t]);

  return (
    <div className="w-full">
      <div
        ref={infoRef}
        style={{
          display: city ? "flex" : "none",
          alignItems: "center",
          borderBottom: "1px solid #e5e7eb",
          fontSize: 13,
          fontWeight: 500,
          flexWrap: "wrap",
          gap: 4,
        }}
      />

      <div
        ref={mapRef}
        className="rounded-b-2xl shadow-lg z-50"
        style={{
          width: "100%",
          height: 400,
          overflow: "hidden",
        }}
      />
    </div>
  );
}
